import { useEffect, useRef, useState } from 'react';
import { HoverTip } from '@/components/ui/HoverTip';
import { cx } from '@/cva.config';

interface TruncatedTextProps {
  align?: 'start' | 'center' | 'end';
  className?: string;
  delay?: number;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  text: string;
}

/**
 * Renders text that truncates with an ellipsis when it overflows, revealing the
 * full string in a HoverTip — but only while it is actually truncated.
 */
export const TruncatedText = ({
  align,
  className,
  delay,
  placement,
  text,
}: TruncatedTextProps) => {
  const spanRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  // Re-measure whenever `text` changes; an empty string can never truncate.
  useEffect(() => {
    const node = spanRef.current;
    if (!node || !text) {
      setIsTruncated(false);
      return;
    }

    const update = () => setIsTruncated(node.scrollWidth > node.clientWidth);
    update();

    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, [text]);

  return (
    <HoverTip
      align={align}
      className="max-w-sm"
      delay={delay}
      disabled={!isTruncated}
      placement={placement}
      text={text}
    >
      <span className={cx('block w-full truncate', className)} ref={spanRef}>
        {text}
      </span>
    </HoverTip>
  );
};
